// 🎨📏 **글자 토막(run) — 렌더 축**(회장님 2026-09-15 «글자 별로 색상이나 폰트 크기»)
// ─────────────────────────────────────────────────────────────────────────────
// 한 오버레이 안에서 **글자마다** 색·크기가 갈릴 수 있게 하는 최소 어휘다. 두 렌더러
// (캔버스 `mediabunnyRender.drawTextOverlays` · remotion `TextOverlayLabelNode`)가 **같은 것을 읽는다**.
//
// ★왜 «구간 인덱스»가 아니라 «토막에 글자를 실어»서 오는가★ — 렌더러에 오는 문자열(`text`)에는
//   줄바꿈이 `\n` 으로 **이미 박혀** 있다(`gen-text-slot-fit` 의 제1 불변식: 두 렌더러가 같은 줄을
//   그리게 하려고 줄을 데이터에 확정한다). 그래서 «원문 몇 번째 글자부터» 같은 인덱스는 렌더 시점에
//   의미가 없다 — 줄바꿈이 끼면서 위치가 밀렸기 때문이다. ⇒ 렌더 축은 **이미 잘라 놓은 토막**을 받고,
//   원문 기준의 구간은 편집 축(`gen-text-run`)이 따로 들고 있는다. `text` ↔ `slotText` 가 두 벌로
//   사는 것과 **정확히 같은 이유·같은 관용구**다.
//
// ★불변식: `runs.map(r => r.text).join("") === text`★ — 두 값이 갈리면 렌더러마다 다른 글자가 나온다.
//   조립하는 곳은 `buildSlotOverlay` 한 군데뿐이고, 스위트가 이 등식을 값으로 잠근다.

/** 🎨 **한 토막** — 이 글자들에만 걸리는 색·크기. 빈 필드 = 오버레이 기본값 그대로. */
export interface MbTextRun {
  /** 이 토막의 글자(줄바꿈 `\n` 을 품을 수 있다 — 줄 나누기는 소비자가 한다). */
  text: string;
  /** 이 토막만의 색. 없으면 오버레이의 `color`. */
  color?: string;
  /** 이 토막만의 크기 **배수**(1 = 기본 크기 그대로). 없으면 1. */
  scale?: number;
}

/** 📏 토막 크기 배수의 허용 범위 — 밖으로 나가면 상자 계산(줄 수·줄 높이)이 무의미해진다. */
export const TEXT_RUN_SCALE_MIN = 0.5;
export const TEXT_RUN_SCALE_MAX = 2;

/**
 * 📏 크기 배수 정리 — 숫자가 아니거나 1이면 `undefined`(= «기본 그대로»).
 * ★1을 저장하지 않는다★: 저장분에 `scale: 1` 이 깔리면 «유저가 크기를 건드렸다»와
 *   «안 건드렸다»를 구분할 수 없어, 되돌리기 창구가 무엇을 지울지 모르게 된다.
 */
export function clampRunScale(v: unknown): number | undefined {
  if (typeof v !== "number" || !Number.isFinite(v)) return undefined;
  const c = Math.min(TEXT_RUN_SCALE_MAX, Math.max(TEXT_RUN_SCALE_MIN, v));
  return Math.abs(c - 1) < 1e-6 ? undefined : c;
}

/** 📏 이 토막들 중 가장 큰 배수(없으면 1) — 줄 높이와 «몇 줄 들어가나»의 근거. */
export function maxRunScale(runs: readonly MbTextRun[] | undefined): number {
  let m = 1;
  for (const r of runs ?? []) m = Math.max(m, clampRunScale(r.scale) ?? 1);
  return m;
}

/**
 * 📐 **토막 → 줄별 토막**. 토막 안의 `\n` 에서 줄을 끊는다.
 *
 * ★두 렌더러가 이 함수 하나를 쓴다★ — 각자 쪼개면 «캔버스는 3줄, remotion 은 4줄» 같은 어긋남이
 *   조용히 생긴다(이 축이 애초에 줄을 데이터에 박은 이유와 같다).
 * 빈 줄도 보존한다(문단 사이 빈 줄이 사라지면 유저가 넣은 간격이 없어진다).
 */
export function splitRunsIntoLines(runs: readonly MbTextRun[]): MbTextRun[][] {
  const lines: MbTextRun[][] = [[]];
  for (const r of runs) {
    const parts = String(r.text ?? "").split("\n");
    parts.forEach((part, i) => {
      if (i > 0) lines.push([]);
      if (part) lines[lines.length - 1].push({ ...r, text: part });
    });
  }
  return lines;
}
